// RepairCostContext.tsx
import React, { createContext, useContext, useEffect, ReactNode, useState } from 'react';
import useGetEstimatedRepairCosts from '../../hooks/api/EstimatedRepairCosts/useGetEstimatedRepairCosts';
import { usePropertyContext } from './PropertyContext';
import { RepairCostContextType } from './DMContextInterfaces';
import { useDecisionFormContext } from './DecisionFormContext';

const RepairCostContext = createContext<RepairCostContextType | undefined>(undefined);

export function RepairCostProvider({ children }: { children: ReactNode }) {
  const { propertyId, isLoadingPropertyDetails } = usePropertyContext();
  const { state: { fixedCosts, userEnteredFixedCosts, propertyId: lspjPropertyId } } = useDecisionFormContext();
  const [ selectedCostSource, setSelectedCostSource ] = useState<'reix' | 'user' | null>('reix');
  const [ manualFixedCosts, setManualFixedCosts ] = useState<number>(fixedCosts);

  const {
    dispatch,
  } = useDecisionFormContext();
  const {
    estimatedRepairCosts,
    isLoadingEstimatedRepairCosts,
    errorEstimatedRepairCosts,
  } = useGetEstimatedRepairCosts(propertyId);

  const estimatedFixedCosts = estimatedRepairCosts?.results?.estimated_fixed_costs;

  useEffect(() => {
    // HELEN TODO: create tests for each scenario of repair costs coming in

    if (propertyId && !isLoadingEstimatedRepairCosts && estimatedFixedCosts !== null
      && userEnteredFixedCosts !== null // by settings this, we know that the lspj has been processed and the fixed costs have been set (if set)
      && lspjPropertyId === propertyId
    ) {

      setManualFixedCosts(null);
      setSelectedCostSource('reix');
      if (fixedCosts === estimatedFixedCosts) {
        // dont set anything
        return;
      }
      if (userEnteredFixedCosts === true) {
        setSelectedCostSource('user');
        setManualFixedCosts(fixedCosts);
      }
      else {
        dispatch({ type: 'INITIALIZE_FORM_KEY', payload: { field: 'fixedCosts', value: estimatedFixedCosts } });
        setSelectedCostSource('reix');
      }
    }
  }, [ estimatedRepairCosts, isLoadingEstimatedRepairCosts, propertyId, userEnteredFixedCosts, lspjPropertyId ]);

  return (
    <RepairCostContext.Provider
      value={{
        calculatedFixedCosts: estimatedFixedCosts || null,
        fixedCostBreakdown: estimatedRepairCosts?.results?.fixed_cost_breakdown ?? null,
        fixedCostsCustomized: !!estimatedRepairCosts?.results?.fixed_costs_customized,
        isLoadingEstimatedRepairCosts: isLoadingEstimatedRepairCosts || isLoadingPropertyDetails,
        errorEstimatedRepairCosts,
        selectedCostSource,
        setSelectedCostSource,
        manualFixedCosts,
        setManualFixedCosts,
      }}>
      {children}
    </RepairCostContext.Provider>
  );
}

export function useRepairCostContext() {
  const ctx = useContext(RepairCostContext);
  if (!ctx) throw new Error('useRepairCostContext must be used within RepairCostProvider');
  return ctx;
}
